import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskPriority } from './tasksApi';
import type { TaskStatusCategory } from './taskStatusesApi';

// --- Types (mirror backend src/modules/daily/daily.types.ts) --------------

export interface DailyCalendarEvent {
  id: string;
  googleEventId: string | null;
  calendarId: string;
  calendarName: string;
  summary: string;
  description: string | null;
  location: string | null;
  startTime: string;
  endTime: string;
  allDay: boolean;
  status: string;
  // Hex of the colour the user picked for this event in Google Calendar, or
  // null when the event inherits its calendar's colour (the common case) —
  // then the inferred category palette below is used instead.
  color: string | null;
}

export interface DailyTask {
  id: string;
  identifier: string | null;
  title: string;
  description: string | null;
  status: string;
  // 0=none, 1=urgent, 2=high, 3=medium, 4=low.
  priority: number;
  // The same status and priority as the task list sees them, for the shared
  // Linear-style row (icons need the category + hex, not the display name).
  priorityLevel: TaskPriority;
  statusId: string;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  dueDate: string | null;
  projectId: string | null;
  projectName: string | null;
  assignee: { id: string; name: string } | null;
  // Retained for API shape compatibility; built-in tasks have no external URL.
  url: string | null;
  // Origin (CLE-59): 'project' for shared project work, 'personal' for a
  // member's personal / CCC Operations work. Badged distinctly.
  source: TaskSource;
}

export type TaskSource = 'project' | 'personal';

export interface GroupedTasks {
  overdue: DailyTask[];
  today: DailyTask[];
  tomorrow: DailyTask[];
  next: DailyTask[];
}

export interface ProjectTaskGroup {
  projectId: string | null;
  projectName: string;
  tasks: DailyTask[];
}

/** AI recommendation derived from the day's calendar (CLE-269). */
export interface DailyRecommendation {
  id: string;
  // Drives the card icon at render time.
  kind: 'prep' | 'time' | 'team' | 'networking' | 'follow_up';
  title: string;
  body: string;
}

export interface DailySummary {
  user: { id: string; name: string; email: string; role: string };
  date: string;
  calendar: { events: DailyCalendarEvent[]; totalEvents: number };
  // AI insights generated from the calendar. Optional: the real backend does
  // not produce daily recommendations (only Test Mode data does), so treat a
  // missing field the same as an empty list.
  recommendations?: DailyRecommendation[];
  tasks: {
    grouped: GroupedTasks;
    byProject: ProjectTaskGroup[];
    statuses: string[];
    total: number;
  };
}

/** One team member's slice of the business-wide overview (CLE-58). */
export interface UserDailySummary {
  user: { id: string; name: string; email: string; role: string; avatarUrl: string | null };
  grouped: GroupedTasks;
  total: number;
}

/** Business-wide ("All Users") Daily Summary — per-user timeline, no calendar. */
export interface BusinessDailySummary {
  date: string;
  users: UserDailySummary[];
}

export interface TeamUser {
  id: string;
  name: string;
  email: string;
  role: string;
  avatarUrl: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Fetch the combined daily summary for a user/date (defaults handled server-side). */
export async function getDailySummary(userId: string, date?: string): Promise<DailySummary> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await apiFetch<Envelope<DailySummary>>(`/api/daily/${userId}${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Fetch the business-wide ("All Users") Daily Summary for a date: every active
 * team member with their active tasks grouped on the due-date timeline. No
 * calendar — that is only shown for an individual user (CLE-58).
 */
export async function getBusinessDailySummary(date?: string): Promise<BusinessDailySummary> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await apiFetch<Envelope<BusinessDailySummary>>(`/api/daily/all-users${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** Fetch the active team members for the user selector. */
export async function listUsers(): Promise<TeamUser[]> {
  const res = await apiFetch<Envelope<TeamUser[]>>('/api/users', { headers: authHeaders() });
  return res.data;
}
